import { useRef, useCallback } from 'react'
import * as THREE from 'three'
import type { MapboxCustomLayerService } from '@/services/3d'
import { FEET_TO_METERS } from '@/constants/units'

export interface AnimationTarget {
  /** Must match the Object3D.name set by AircraftLoaderService (= allocationId) */
  aircraftId: string
  /** Target position in feet [x, y, z] */
  toPosition: [number, number, number]
  /** Target heading in degrees */
  toRotation: number
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

/** Shortest-path interpolation for rotation (degrees) */
function lerpAngle(from: number, to: number, t: number): number {
  const delta = ((to - from) % 360 + 540) % 360 - 180
  return from + delta * t
}

/**
 * Hook providing an imperative `animateToScenario` function.
 * Directly mutates Three.js Object3D transforms and calls markSceneDirty()
 * each frame — no React state updates during animation.
 */
export function useAnimationEngine(
  layerServiceRef: React.RefObject<MapboxCustomLayerService | null>
) {
  const rafRef = useRef<number | null>(null)

  const animateToScenario = useCallback(
    (targets: AnimationTarget[], durationMs = 1200): Promise<void> => {
      return new Promise((resolve) => {
        const layerService = layerServiceRef.current
        if (!layerService) { resolve(); return }

        const sceneObjects = layerService.getSceneObjects?.()
        if (!sceneObjects?.aircraft?.length && !sceneObjects?.scene) { resolve(); return }

        // Cancel any running animation
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }

        // Snapshot starting state for each target
        type Snapshot = {
          obj: THREE.Object3D
          fromPos: THREE.Vector3
          toPos: THREE.Vector3
          fromRot: number // degrees
          toRot: number   // degrees
        }

        const snapshots: Snapshot[] = []
        const scene = sceneObjects.scene as THREE.Scene

        for (const target of targets) {
          // Aircraft objects are named by allocationId
          let obj: THREE.Object3D | undefined = sceneObjects.aircraft.find(
            (a) => a.name === target.aircraftId
          )

          if (!obj) {
            // Also search recursively in the scene (covers parent groups)
            scene.traverse((child) => {
              if (!obj && child.name === target.aircraftId) obj = child
            })
          }

          if (!obj) continue

          const toPos = new THREE.Vector3(
            target.toPosition[0] * FEET_TO_METERS,
            target.toPosition[1] * FEET_TO_METERS,
            target.toPosition[2] * FEET_TO_METERS
          )

          snapshots.push({
            obj,
            fromPos: obj.position.clone(),
            toPos,
            fromRot: (obj.rotation.y * 180) / Math.PI,
            toRot: target.toRotation,
          })
        }

        if (snapshots.length === 0) { resolve(); return }

        const startTime = performance.now()

        const tick = () => {
          const elapsed = performance.now() - startTime
          const raw = Math.min(elapsed / durationMs, 1)
          const t = easeInOutCubic(raw)

          for (const snap of snapshots) {
            snap.obj.position.lerpVectors(snap.fromPos, snap.toPos, t)
            snap.obj.rotation.y =
              (lerpAngle(snap.fromRot, snap.toRot, t) * Math.PI) / 180
          }

          layerService.markSceneDirty()

          if (raw < 1) {
            rafRef.current = requestAnimationFrame(tick)
          } else {
            rafRef.current = null
            resolve()
          }
        }

        rafRef.current = requestAnimationFrame(tick)
      })
    },
    [layerServiceRef]
  )

  return { animateToScenario }
}
